import React from "react";
import {TFunction} from "i18next";
import {useTranslation} from "react-i18next";
import Table, {TableProps} from "@awsui/components-react/table";
import Box from "@awsui/components-react/box";
import Header from "@awsui/components-react/header";
import Rider from "model/Rider";
import useTable from "hooks/useTable";
import Pagination from "@awsui/components-react/pagination";
import CollectionPreferences from "@awsui/components-react/collection-preferences";
import {formatNumber} from "common/formatNumber";
import RiderPopover from "components/RiderPopover";


interface AllLineupsTableProps {
    username: string;
    riders: Rider[];
    points: number;
}

const columnDefinitions = (t: TFunction, tCommon: TFunction): TableProps.ColumnDefinition<Rider>[] => [
    {
        id: "name",
        header: t("lineupsTable.columns.name"),
        cell: (rider: Rider) => <RiderPopover rider={rider} />,
        sortingField: "name"
    },
    {
        id: "value",
        header: t("lineupsTable.columns.value"),
        cell: (rider: Rider) => rider.value != null ? `${formatNumber(rider.value, 1)} ${tCommon("common.million")}` : "-",
        sortingField: "value"
    },
    {
        id: "points",
        header: t("lineupsTable.columns.points"),
        cell: (rider: Rider) => rider.points ?? 0,
        sortingField: "points"
    }
];

const AllLineupsTable: React.FC<AllLineupsTableProps> = (props) => {

    const {t} = useTranslation(["standings", "common"]);
    const {t: tCommon} = useTranslation("common");

    const {
        preferences,
        setPreferences,
        items,
        collectionProps,
        paginationProps,
        collectionPreferencesProps
    } = useTable(props.riders || [], {
        emptyStateSubtitle: new Error(),
        emptyStateTitle: t("standings:lineupsTable.empty"),
        pageSize: 12
    });

    return (
        <Table
            {...collectionProps}
            loadingText={t("standings:lineupsTable.loading")}
            columnDefinitions={columnDefinitions(t, tCommon)}
            items={items}
            loading={false}
            wrapLines={false}
            pagination={
                <Pagination
                    {...paginationProps}
                    ariaLabels={{
                        nextPageLabel: tCommon("table.nextPage"),
                        previousPageLabel: tCommon("table.previousPage"),
                        pageLabel: pageNumber => tCommon("table.page", {page: pageNumber})
                    }}
                />
            }
            preferences={
                <CollectionPreferences
                    {...collectionPreferencesProps}
                    preferences={preferences}
                    onConfirm={({detail}) => setPreferences(detail)}
                />
            }
            header={<Header variant="h2">{props.username}</Header>}
            footer={<Box variant="h4" textAlign="right" color="text-status-info">{props.points} {tCommon("common.points")}</Box>}
        />
    );
};

export default AllLineupsTable;
